import React from "react";
import type { NavigationConfig } from "@/lib/components/navigation/types";
import { NotificationBellNav } from "../components/navigation";
import type { UserNotification } from "../types";

/**
 * Utility functions to help integrate notifications into navigation configurations
 * and handle notification navigation based on referenceType and referenceId
 */

/**
 * Reference types supported by the notification system
 */
export type NotificationReferenceType =
  | "APPOINTMENT"
  | "WAITING_LIST"
  | "WORKER_SCHEDULE"
  | "CUSTOMER"
  | "SERVICE"
  | "WORKER"
  | "SALON_SETTINGS"
  | "SYSTEM"
  | "MESSAGE"
  | "AUDIT_LOG";

/**
 * Maps reference types to their corresponding navigation paths
 */
export const getReferenceNavigationPath = (
  referenceType: string,
  _referenceId: string
): string | null => {
  switch (referenceType.toUpperCase()) {
    case "APPOINTMENT":
      // Navigate to appointments page - in future this could be a specific appointment view
      return "/appointments";

    case "WAITING_LIST":
      // Navigate to appointments page where waiting list is managed
      return "/appointments";

    case "WORKER_SCHEDULE":
    case "WORKER":
      // Navigate to workers page - could be specific worker view in future
      return "/workers";

    case "CUSTOMER":
      // Navigate to customers page - could be specific customer view in future
      return "/customers";

    case "SERVICE":
      // Navigate to services page - could be specific service edit in future
      return "/services";

    case "SALON_SETTINGS":
      // Navigate to salon settings
      return "/salon";

    case "MESSAGE":
      // Navigate to messages page
      return "/messages";

    case "AUDIT_LOG":
      // Navigate to audit page
      return "/audit";
    default:
      // For system notifications or unknown types, go to notifications page
      return "/notifications";
  }
};

/**
 * Gets a human-readable display name for a reference type
 */
export const getReferenceTypeDisplayName = (referenceType: string): string => {
  switch (referenceType.toUpperCase()) {
    case "APPOINTMENT":
      return "Appointment";
    case "WAITING_LIST":
      return "Waiting List";
    case "WORKER_SCHEDULE":
      return "Worker Schedule";
    case "CUSTOMER":
      return "Customer";
    case "SERVICE":
      return "Service";
    case "WORKER":
      return "Staff Member";
    case "SALON_SETTINGS":
      return "Salon Settings";
    case "SYSTEM":
      return "System";
    case "MESSAGE":
      return "Message";
    case "AUDIT_LOG":
      return "Audit Log";
    default:
      return referenceType;
  }
};

/**
 * Handles navigation for a notification based on its referenceType and referenceId
 * Falls back to actionUrl if reference navigation is not available
 * Supports both dialog opening and page navigation
 */
export const handleNotificationNavigation = (
  notification: UserNotification,
  onNavigate?: (path: string) => void,
  onOpenDialog?: (type: string, id: string, params?: Record<string, any>) => void
): void => {
  // Special handling for appointment references - prefer dialog over page navigation
  if (
    notification.referenceType?.toUpperCase() === "APPOINTMENT" &&
    notification.referenceId &&
    onOpenDialog
  ) {
    onOpenDialog("appointment-detail", notification.referenceId);
    return;
  }

  let navigationPath: string | null = null;

  // Try to navigate based on referenceType and referenceId
  if (notification.referenceType && notification.referenceId) {
    navigationPath = getReferenceNavigationPath(
      notification.referenceType,
      notification.referenceId
    );
  }

  // Fallback to actionUrl if provided and no reference navigation available
  if (!navigationPath && notification.actionUrl) {
    if (notification.actionUrl.startsWith("/")) {
      navigationPath = notification.actionUrl;
    } else {
      // External URL - open in new tab
      window.open(notification.actionUrl, "_blank");
      return;
    }
  }

  // Navigate using provided callback or default window navigation
  if (navigationPath) {
    if (onNavigate) {
      onNavigate(navigationPath);
    } else {
      window.location.href = navigationPath;
    }
  }
};

/**
 * Checks if a notification has actionable navigation
 */
export const isNotificationActionable = (notification: UserNotification): boolean => {
  return !!((notification.referenceType && notification.referenceId) || notification.actionUrl);
};

/**
 * Adds a notification bell to the trailing elements of a navigation config
 */
export function withNotificationBell(
  config: NavigationConfig,
  options: {
    position?: "first" | "last";
    className?: string;
  } = {}
): NavigationConfig {
  const { position = "last", className } = options;

  const notificationBell = <NotificationBellNav className={className} key="notification-bell" />;

  const existingTrailing = config.trailingElements || [];
  const newTrailing =
    position === "first"
      ? [notificationBell, ...existingTrailing]
      : [...existingTrailing, notificationBell];

  return {
    ...config,
    trailingElements: newTrailing,
  };
}

/**
 * Creates a navigation config with notification bell included by default
 */
export function createNotificationAwareNavConfig(
  baseConfig: Omit<NavigationConfig, "trailingElements"> & {
    trailingElements?: React.ReactNode[];
  },
  options: {
    includeNotifications?: boolean;
    notificationPosition?: "first" | "last";
    notificationClassName?: string;
  } = {}
): NavigationConfig {
  const {
    includeNotifications = true,
    notificationPosition = "last",
    notificationClassName,
  } = options;

  if (!includeNotifications) {
    return baseConfig as NavigationConfig;
  }

  return withNotificationBell(baseConfig as NavigationConfig, {
    position: notificationPosition,
    className: notificationClassName,
  });
}

/**
 * Pre-configured navigation configs for common pages
 */
export const notificationNavConfigs = {
  dashboard: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Dashboard",
      subtitle: "Overview of your salon activity",
      trailingElements: additionalTrailing,
    }),

  appointments: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Appointments",
      subtitle: "Manage your salon appointments",
      trailingElements: additionalTrailing,
    }),

  calendar: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Calendar",
      subtitle: "Schedule and manage appointments",
      trailingElements: additionalTrailing,
    }),

  customers: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Customers",
      subtitle: "Manage customer profiles and history",
      trailingElements: additionalTrailing,
    }),

  workers: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Staff",
      subtitle: "Manage salon staff and schedules",
      trailingElements: additionalTrailing,
    }),

  services: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Services",
      subtitle: "Manage salon services and pricing",
      trailingElements: additionalTrailing,
    }),

  settings: (additionalTrailing: React.ReactNode[] = []) =>
    withNotificationBell({
      title: "Settings",
      subtitle: "Configure salon preferences",
      trailingElements: additionalTrailing,
    }),
};
